import { create } from "zustand";
import type { ReactNode } from "react";

// Cortina de cambio de app (ver components/ui/AppTransitionOverlay).
//
// Por qué existe: al pasar del mapa a Administración de Flotilla el cambio era
// instantáneo y la app destino se veía armándose a medias. La navegación
// ocurre DEBAJO de la cortina, y la cortina se va cuando la app destino
// terminó de montar, no tras un tiempo fijo:
//   0 ms        entra (ENTER_MS)
//   ENTER_MS    se navega: la app destino monta tapada
//   después     dos cuadros pintados + hilo principal libre (requestIdleCallback)
//               = el render síncrono de la app destino ya terminó
//   ≥ MIN       sale (EXIT_MS)
// Tiempos ajustados a pedido del usuario (5-oct-2026), sobre una versión que
// entraba en 150 ms y salía en cuanto podía: el spinner daba media vuelta y
// "parecía un bug", y la app de origen desaparecía de golpe. Cambiar de app es
// poco frecuente (decenas al día, no cientos) y quien la usa no debe sentirse
// apurado:
// - ENTER_MS 300: la app de origen se desvanece, no se corta.
// - MIN_VISIBLE_MS 1400: el indicador da ~1.75 vueltas (0.8 s cada una) y se
//   alcanza a leer "Abriendo <app>…". Si la app destino tarda más, se espera a
//   que termine de montar (no es un tope, es un piso).
// - EXIT_MS 400: la app destino aparece con calma.
// READY_TIMEOUT_MS evita quedarse tapado si el hilo nunca se libera.
export const ENTER_MS = 300;
export const MIN_VISIBLE_MS = 1400;
export const READY_TIMEOUT_MS = 4000;
export const EXIT_MS = 400;

type Phase = "idle" | "in" | "out";

interface AppTransitionState {
  phase: Phase;
  name: string;
  icon: ReactNode;
  isDarkMode: boolean;
  start: (app: { name: string; icon: ReactNode }, isDarkMode: boolean, go: () => void) => void;
}

const afterRenderSettles = (cb: () => void) => {
  let done = false;
  const finish = () => {
    if (!done) {
      done = true;
      cb();
    }
  };
  setTimeout(finish, READY_TIMEOUT_MS);
  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      const ric = (window as Window & { requestIdleCallback?: (f: () => void, o?: { timeout: number }) => number })
        .requestIdleCallback;
      if (ric) ric(finish, { timeout: READY_TIMEOUT_MS });
      else setTimeout(finish, 0);
    }),
  );
};

export const useAppTransitionStore = create<AppTransitionState>((set, get) => ({
  phase: "idle",
  name: "",
  icon: null,
  isDarkMode: false,
  start: (app, isDarkMode, go) => {
    // Un segundo clic durante un cambio en curso se ignora.
    if (get().phase !== "idle") return;
    const t0 = performance.now();
    set({ phase: "in", name: app.name, icon: app.icon, isDarkMode });
    setTimeout(() => {
      go();
      afterRenderSettles(() => {
        const wait = Math.max(0, MIN_VISIBLE_MS - (performance.now() - t0));
        setTimeout(() => {
          set({ phase: "out" });
          setTimeout(() => set({ phase: "idle", name: "", icon: null }), EXIT_MS);
        }, wait);
      });
    }, ENTER_MS);
  },
}));
